import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
    Play,
    Square,
    Activity,
    X,
    AlertCircle,
    Lock,
    Brain,
    ShoppingCart
} from 'lucide-react';
import { LiveStats } from './LiveStats';
import { LiveSuggestions } from './LiveSuggestions';
import { LiveActions } from './LiveActions';
import { LiveFollowers } from './LiveFollowers';
import { LiveOrders } from './LiveOrders';
import { LiveJp } from './LiveJp';
import { isLiveAssistantEnabled } from '@/shared/config/featureFlags';

// Type pour les commandes
interface OrderSummary {
    id: string;
    customer: string;
    time: string;
    products: Array<{
        name: string;
        size: string;
        status: 'paid' | 'pending';
        price: string;
    }>;
}
import { LiveAssistantRepository, SuggestionDoc, SnapshotDoc, JpEvent, LiveSession, CreatorLiveStatus } from '@/services/firebase/live-assistant-repository';
import { OrderRepository } from '@/services/firebase/order-repository';
import { FirebaseOrder } from '@/models';
import { toast } from 'react-hot-toast';
import SeoTags from '@/components/shared/SeoTags';
import { useTheme } from '@/shared/contexts/ThemeContext';
import { useOrgaConfigurationContext } from '@/app/providers';
import { useTranslation } from 'react-i18next';
import { BuyCreditsModal } from '@/features/admin/components/BuyCreditsModal';

// Utiliser le proxy en développement, l'URL directe en production
// En développement, utiliser les routes proxy définies dans vite.config.ts
// En production, utiliser l'URL de l'API live-assistant
const LIVE_ASSISTANT_BASE_URL = import.meta.env.VITE_BASE_URL_LIVE_ASSISTANT_API || '';

export const LiveAssistant = () => {
    const { idorga } = useParams<{ idorga: string }>();
    const { theme } = useTheme();
    const { t } = useTranslation();
    const [liveSession, setLiveSession] = useState<LiveSession | null>(null);
    const [creatorStatus, setCreatorStatus] = useState<CreatorLiveStatus | null>(null);

    // Configuration et Plan
    const { configuration, loading: configLoading } = useOrgaConfigurationContext();

    // Vérifier si la fonctionnalité est activée pour cette organisation
    const liveAssistantEnabled = isLiveAssistantEnabled(idorga, configuration);

    // Si la fonctionnalité n'est pas activée, afficher un message
    if (!liveAssistantEnabled && !configLoading) {
        return (
            <div className="h-full flex flex-col items-center justify-center bg-background p-8">
                <div className="text-center max-w-md">
                    <div className="w-16 h-16 bg-background-secondary rounded-full flex items-center justify-center mx-auto mb-6">
                        <Lock className="h-8 w-8 text-foreground-secondary" />
                    </div>
                    <h2 className="text-xl font-bold text-foreground mb-3">
                        {t('admin.liveAssistant.unavailable.title')}
                    </h2>
                    <p className="text-foreground-secondary mb-6">
                        {t('admin.liveAssistant.unavailable.description')}
                    </p>
                    <a
                        href={`/orga/${idorga}/admin`}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors"
                    >
                        {t('admin.liveAssistant.unavailable.backToDashboard')}
                    </a>
                </div>
            </div>
        );
    }

    const toDate = (v: any): Date | null => {
        if (!v) return null;
        if (v?.toDate) {
            try { return v.toDate(); } catch { return null; }
        }
        if (typeof v === 'string') {
            const d = new Date(v);
            return isNaN(d.getTime()) ? null : d;
        }
        if (v instanceof Date) return v;
        return null;
    };
    const isLive = useMemo(() => {
        return creatorStatus?.state === 'RUNNING';
    }, [creatorStatus?.state]);

    // Stabiliser les valeurs importantes pour éviter les re-souscriptions inutiles
    const currentSessionId = useMemo(() => creatorStatus?.currentSessionId, [creatorStatus?.currentSessionId]);
    const creatorState = useMemo(() => creatorStatus?.state, [creatorStatus?.state]);
    const creatorOrgId = useMemo(() => creatorStatus?.organisationId, [creatorStatus?.organisationId]);

    const [isStopping, setIsStopping] = useState(false);
    const [isStarting, setIsStarting] = useState(false);
    const [showPurchaseModal, setShowPurchaseModal] = useState(false);

    // Configuration et Plan
    // Retiré car déplacé en haut pour le check de permission
    const subscriptionConfig = configuration?.subscriptionConfig;
    const planType = subscriptionConfig?.planType || 'FREEMIUM';
    const isIAUnlimited = planType === 'PRO';

    // Vérifier si l'organisation a des crédits (via creditsBalance)
    const creditsBalance = configuration?.creditsBalance;
    const balanceCents = creditsBalance?.balanceCents ?? 0;
    const hasCredits = balanceCents > 0;

    // Vérifier si Stripe Connect est activé (permet de déduire automatiquement des paiements)
    const stripeConnectEnabled = !!configuration?.stripeConnectConfig?.accountId;

    // L'utilisateur peut lancer le live si :
    // 1. Il a un plan PRO (IA illimitée) OU
    // 2. Il a des crédits OU
    // 3. Stripe Connect est activé (les frais seront déduits du paiement)
    const canStartLive = isIAUnlimited || hasCredits || stripeConnectEnabled;

    // Utiliser l'ID FireStore réel de la configuration
    const orgaConfigId = configuration?.id || null;

    // Utiliser des refs pour éviter les re-souscriptions inutiles
    const subscriptionRef = useRef<{
        sessionId?: string;
        orgId?: string;
        state?: string;
    }>({});

    // Real-time Data States
    const [suggestionDoc, setSuggestionDoc] = useState<SuggestionDoc | null>(null);
    const [snapshotDoc, setSnapshotDoc] = useState<SnapshotDoc | null>(null);
    const [jpEventsRaw, setJpEventsRaw] = useState<JpEvent[]>([]);
    const [ordersRaw, setOrdersRaw] = useState<FirebaseOrder[]>([]);
    const [tiktokUsername, setTiktokUsername] = useState('');
    const [startError, setStartError] = useState<string | null>(null);
    const [isForcingStop, setIsForcingStop] = useState(false);


    // 1. Subscribe to CreatorLiveStatus
    useEffect(() => {
        if (!orgaConfigId) return;
        const unsub = LiveAssistantRepository.subscribeToCreatorLiveStatus(orgaConfigId, setCreatorStatus);
        return () => unsub();
    }, [orgaConfigId]);

    // 2. Subscribe to LiveSession and Data when active
    useEffect(() => {
        if (!orgaConfigId) {
            setLiveSession(null);
            return;
        }

        // Vérifier si les valeurs ont vraiment changé pour éviter les re-souscriptions inutiles
        const orgIdToUse = creatorOrgId || orgaConfigId;
        const shouldResubscribe =
            subscriptionRef.current.sessionId !== currentSessionId ||
            subscriptionRef.current.orgId !== orgIdToUse ||
            subscriptionRef.current.state !== creatorState;

        // S'abonner à la session si currentSessionId est disponible
        let unsubSession = () => { };
        if (currentSessionId && shouldResubscribe) {
            subscriptionRef.current.sessionId = currentSessionId;
            console.log('📡 Subscribing to session:', currentSessionId);
            unsubSession = LiveAssistantRepository.subscribeToLiveSessionById(currentSessionId, setLiveSession);
        } else if (!currentSessionId) {
            subscriptionRef.current.sessionId = undefined;
            console.log('⚠️ No currentSessionId in CreatorLiveStatus, skipping session subscription');
            setLiveSession(null);
        }

        let unsubSuggestions = () => { };
        let unsubSnapshots = () => { };
        let unsubJp = () => { };
        let unsubOrders = () => { };

        // S'abonner aux données même si currentSessionId est null, tant que le live est RUNNING
        if (creatorState === 'RUNNING') {
            if (shouldResubscribe || subscriptionRef.current.orgId !== orgIdToUse) {
                subscriptionRef.current.state = creatorState;
                subscriptionRef.current.orgId = orgIdToUse;

                console.log('📡 Live RUNNING → subscribing to suggestions/snapshots/jp/orders');

                if (!orgIdToUse) {
                    console.error('❌ No organisationId available for subscriptions!');
                    return;
                }

                unsubSuggestions = LiveAssistantRepository.subscribeToSuggestions(orgIdToUse, setSuggestionDoc);
                unsubSnapshots = LiveAssistantRepository.subscribeToSnapshots(orgIdToUse, setSnapshotDoc);
                unsubJp = LiveAssistantRepository.subscribeToJpEvents(orgIdToUse, setJpEventsRaw);
                unsubOrders = OrderRepository.subscribeToOrdersByLastProductCreatedAt(setOrdersRaw);
            }
        } else {
            // Nettoyer les subscriptions si le live n'est plus actif
            if (subscriptionRef.current.state === 'RUNNING') {
                subscriptionRef.current.state = creatorState;
                setSuggestionDoc(null);
                setSnapshotDoc(null);
                setJpEventsRaw([]);
            }
        }

        return () => {
            if (shouldResubscribe) {
                console.log('🔌 Unsubscribing from data streams');
                unsubSession();
                unsubSuggestions();
                unsubSnapshots();
                unsubJp();
                unsubOrders();
            }
        };
    }, [orgaConfigId, currentSessionId, creatorState, creatorOrgId]);

    // 3. Client Heartbeat
    useEffect(() => {
        if (!isLive || !orgaConfigId) return;

        // Utiliser l'organisationId du CreatorLiveStatus si disponible
        const orgIdToUse = creatorStatus?.organisationId || orgaConfigId;

        const sendHeartbeat = async () => {
            try {
                const response = await fetch(`${LIVE_ASSISTANT_BASE_URL}/heartbeat/${orgIdToUse}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ clientId: 'web-client' })
                });

                if (!response.ok) {
                    console.warn('⚠️ Heartbeat failed:', response.status, response.statusText);
                } else {
                    console.log('💓 Client Heartbeat sent');
                }
            } catch (err) {
                console.error("Failed to send heartbeat", err);
            }
        };

        // Send immediately then every 25s (to be safe within 60s timeout)
        sendHeartbeat();
        const interval = setInterval(sendHeartbeat, 25000);

        return () => clearInterval(interval);
    }, [isLive, orgaConfigId, creatorStatus?.organisationId]);

    // --- Data Transformation ---

    // 1. Stats (from suggestionDoc which contains the latest analysis)
    const stats = useMemo(() => {
        // Priority 1: Use data from suggestionDoc (most recent analysis with AI context)
        if (suggestionDoc && suggestionDoc.qualityLabel && suggestionDoc.trendLabel) {
            const thermal = suggestionDoc.qualityLabel;
            const trend = suggestionDoc.trendLabel;
            const audience = suggestionDoc.audienceLabel || 'mixed';
            const signals = suggestionDoc.reason ? [suggestionDoc.reason.replace('trigger:', '')] : [];

            return { thermal, trend, audience, signals };
        }

        // Priority 2: Fallback to snapshotDoc if no suggestion yet
        if (snapshotDoc && snapshotDoc.trend) {
            let thermal = 'normal';
            if (snapshotDoc.trend === 'drop') thermal = 'cold';
            if (snapshotDoc.trend === 'rising') thermal = 'hot';

            return {
                thermal,
                trend: snapshotDoc.trend,
                audience: 'mixed',
                signals: []
            };
        }

        // No data available
        return null;
    }, [suggestionDoc, snapshotDoc]);

    // 2. Suggestions (Parse raw string)
    const suggestions = useMemo(() => {
        if (!suggestionDoc?.suggestionsRaw) return [];

        // Remove the "Actions IA" section first
        const textWithoutActions = suggestionDoc.suggestionsRaw.split('👉 Actions IA :')[0].trim();

        // Match pattern: (🔵 Taux de réussite : 80%) Text content
        const regex = /\((🔵|🟢|🟡)[^)]*?(\d+)%\)\s*(.+?)(?=\(🔵|\(🟢|\(🟡|$)/gs;
        const parsed = [];
        let match;

        while ((match = regex.exec(textWithoutActions)) !== null) {
            const emoji = match[1];
            const score = parseInt(match[2], 10);
            const text = match[3].trim();

            // Determine type based on emoji
            let type = 'interaction';
            if (emoji === '🔵') type = 'style';
            if (emoji === '🟢') type = 'interaction';
            if (emoji === '🟡') type = 'jp';

            if (text) {
                parsed.push({
                    id: parsed.length + 1,
                    text,
                    type,
                    score
                });
            }
        }

        // Fallback if regex fails
        if (parsed.length === 0 && textWithoutActions) {
            return [{ id: 1, text: textWithoutActions, type: 'ia', score: 85 }];
        }

        return parsed;
    }, [suggestionDoc]);

    // Helper to detect action type from text
    const detectActionType = (text: string): string => {
        const lowerText = text.toLowerCase();
        if (lowerText.includes('jeu') || lowerText.includes('défi') || lowerText.includes('quiz')) return 'jeu';
        if (lowerText.includes('jp') || lowerText.includes('juste prix') || lowerText.includes('réserv')) return 'jp';
        if (lowerText.includes('question') || lowerText.includes('demande') || lowerText.includes('invite')) return 'interaction';
        if (lowerText.includes('montre') || lowerText.includes('présente') || lowerText.includes('produit')) return 'product';
        if (lowerText.includes('style') || lowerText.includes('porter') || lowerText.includes('look')) return 'style';
        return 'general';
    };

    // 3. Actions (Parse from suggestionsRaw)
    const actions = useMemo(() => {
        if (!suggestionDoc?.suggestionsRaw) return [];

        // Extract actions from the "👉 Actions IA :" section
        const actionsMatch = suggestionDoc.suggestionsRaw.match(/👉 Actions IA :\s*([\s\S]*?)(?:\n\n|$)/);

        if (!actionsMatch) {
            // Fallback: generate based on trigger if no actions in raw
            const trigger = suggestionDoc?.reason || '';
            const acts = [];

            if (trigger.includes('DANGEROUS_DROP')) {
                acts.push({ id: 1, text: "Lance un jeu ou un concours immédiatement !", type: "engagement" });
                acts.push({ id: 2, text: "Change de produit ou de sujet", type: "product" });
            } else if (trigger.includes('SILENCE') || trigger.includes('CHAT_DEAD')) {
                acts.push({ id: 1, text: "Pose une question ouverte au chat", type: "interaction" });
                acts.push({ id: 2, text: "Lance un mini défi likes", type: "engagement" });
            } else if (trigger.includes('JP_WAVE')) {
                acts.push({ id: 1, text: "Rappelle les règles du JP", type: "jp" });
                acts.push({ id: 2, text: "Valorise les personnes qui ont réservé", type: "interaction" });
            } else if (trigger.includes('NEW_VIEWERS')) {
                acts.push({ id: 1, text: "Accueille les nouveaux et explique le JP", type: "interaction" });
                acts.push({ id: 2, text: "Présente rapidement la collection", type: "product" });
            } else {
                acts.push({ id: 1, text: "Continue comme ça, le live est stable", type: "general" });
            }
            return acts;
        }

        // Parse actions from the raw text
        const actionsText = actionsMatch[1];
        const actionLines = actionsText.split('\n')
            .filter(line => line.trim().startsWith('- Action'))
            .map(line => line.replace(/^- Action \d+ : /, '').trim());

        return actionLines.map((text, index) => ({
            id: index + 1,
            text,
            type: detectActionType(text)
        }));
    }, [suggestionDoc, detectActionType]);

    // 4. Followers
    const followers = useMemo(() => {
        if (!snapshotDoc?.topEngaged) return [];
        return snapshotDoc.topEngaged.slice(0, 5).map(f => ({
            nickname: f.nickname,
            score: f.score,
            messages: f.messages,
            presenceMs: f.presenceMs,
            likes: f.likes,
            gifts: f.gifts
        }));
    }, [snapshotDoc]);

    // 5. JP Events (Filter last 5 minutes)
    const jpEvents = useMemo(() => {
        const now = new Date();
        const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);

        return jpEventsRaw
            .filter(e => {
                const eventDate = new Date(e.messageTs);
                return eventDate >= fiveMinutesAgo;
            })
            .map(e => {
                const date = new Date(e.messageTs);
                const diffMs = now.getTime() - date.getTime();
                const diffMins = Math.floor(diffMs / 60000);
                const diffSecs = Math.floor((diffMs % 60000) / 1000);

                return {
                    id: e.id,
                    nickname: e.nickname,
                    uniqueId: e.uniqueId,
                    userId: e.userId,
                    item: e.parsed || e.comment || 'JP',
                    comment: e.comment,
                    timestamp: date.getTime(),
                    timeAgo: diffMins < 1
                        ? (diffSecs < 10 ? "à l'instant" : `il y a ${diffSecs}s`)
                        : `il y a ${diffMins} min`
                };
            });
    }, [jpEventsRaw]);

    // 6. Orders → unwrap into product lines, sorted by lastProductCreatedAt
    const orders = useMemo(() => {
        const now = new Date();
        const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);

        const toDate = (v: any): Date => {
            if (!v) return new Date(0);
            if (v instanceof Date) return v;
            if (v?.toDate) {
                try { return v.toDate(); } catch { return new Date(0); }
            }
            if (typeof v === 'string') {
                const d = new Date(v);
                return isNaN(d.getTime()) ? new Date(0) : d;
            }
            return new Date(0);
        };

        // Orders are already sorted by lastProductCreatedAt desc by Firestore query
        const sortedOrders = ordersRaw;

        // Unwrap to individual product lines (paid or pending) within last 5 minutes window
        const productLines = [] as OrderSummary[];

        for (const o of sortedOrders) {
            const orderLastTs = toDate((o as any).lastProductCreatedAt || o.updatedAt || o.createdAt);
            if (orderLastTs < fiveMinutesAgo) continue;

            const customerLabel = `${o.orderInfo.firstName} ${o.orderInfo.lastName?.charAt(0)}.`;

            for (let idx = 0; idx < o.products.length; idx++) {
                const p = o.products[idx];
                if (!(p.paymentInfo?.status === 'paid' || p.paymentInfo?.status === 'pending')) continue;

                const pDate = toDate(p.createdAt || o.createdAt);
                const diffMins = Math.floor((now.getTime() - pDate.getTime()) / 60000);

                productLines.push({
                    id: `${o.id || '?'}-${idx}`,
                    customer: customerLabel,
                    time: diffMins < 1 ? "à l'instant" : `${diffMins} min`,
                    products: [{
                        name: p.model || p.reference || 'Produit',
                        size: p.reference?.split(' ')?.[1] || '',
                        status: (p.paymentInfo?.status === 'paid' || p.paymentInfo?.status === 'pending'
                            ? p.paymentInfo.status
                            : 'pending') as 'paid' | 'pending',
                        price: `${p.amount}€`
                    }]
                });
            }
        }

        // Limit to most recent 10 lines
        return productLines.slice(0, 10);
    }, [ordersRaw]);

    const isUsernameActive = useMemo(() => {
        if (!tiktokUsername || !liveSession) return false;
        const a = (liveSession.uniqueId || '').toLowerCase();
        const b = (liveSession.tiktokUsername || '').toLowerCase();
        const u = tiktokUsername.toLowerCase();
        return liveSession.isActive && (a === u || b === u);
    }, [tiktokUsername, liveSession]);

    // No auto pre-fill of TikTok username

    const handleBuyCredits = () => {
        setShowPurchaseModal(true);
    };

    const handleClosePurchaseModal = () => {
        setShowPurchaseModal(false);
    };

    const lastDataTs = useMemo(() => {
        const s1 = suggestionDoc?.createdAt ? toDate((suggestionDoc as any).createdAt) : null;
        const s2 = snapshotDoc?.createdAt ? toDate((snapshotDoc as any).createdAt) : null;
        const ms1 = s1 ? s1.getTime() : 0;
        const ms2 = s2 ? s2.getTime() : 0;
        return Math.max(ms1, ms2);
    }, [suggestionDoc, snapshotDoc]);

    const lastHeartbeatLabel = useMemo(() => {
        const hb = toDate((liveSession as any)?.lastUpdated);
        if (!hb) return '';
        return hb.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    }, [liveSession]);

    const isDesync = useMemo(() => {
        // Ne pas afficher pendant le démarrage
        if (!isLive || isStarting || !liveSession) return false;

        // Si pas de données encore, attendre 30 secondes avant de considérer comme désynchronisé
        if (!lastDataTs) {
            // Vérifier si le live vient de démarrer (dans les 30 premières secondes)
            const sessionStartTime = liveSession.liveStartedAt ? new Date(liveSession.liveStartedAt).getTime() : null;
            if (sessionStartTime) {
                const timeSinceStart = Date.now() - sessionStartTime;
                // Période de grâce de 30 secondes au démarrage
                if (timeSinceStart < 30 * 1000) {
                    return false;
                }
            }
            // Après 30 secondes sans données, considérer comme désynchronisé
            return true;
        }

        const age = Date.now() - lastDataTs;
        // Désynchronisation si aucune donnée depuis plus de 2 minutes
        return age > 2 * 60 * 1000;
    }, [isLive, isStarting, liveSession, lastDataTs]);

    useEffect(() => {
        if (isDesync) {
            console.warn('⚠️ Possible désynchronisation: aucune donnée récente alors que isLive=true');
        }
    }, [isDesync]);

    const forceStop = async () => {
        if (!idorga || !orgaConfigId || !isLive) return;
        try {
            setIsForcingStop(true);
            const res = await fetch(`${LIVE_ASSISTANT_BASE_URL}/stop-live/${orgaConfigId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });
            if (!res.ok) {
                let msg = 'Erreur lors de l\'arrêt forcé';
                try {
                    const data = await res.json();
                    msg = data?.error || data?.message || msg;
                } catch { }
                toast.error(msg);
                return;
            }
            setLiveSession(null);
            toast.success('Arrêt forcé effectué');
            console.log('🛑 Arrêt forcé effectué');
        } catch (error) {
            const msg = (error as any)?.message || 'Erreur lors de l\'arrêt forcé';
            toast.error(msg);
        } finally {
            setIsForcingStop(false);
        }
    };

    const toggleLive = async () => {
        if (!idorga) {
            toast.error('ID d\'organisation manquant');
            return;
        }

        if (!orgaConfigId) {
            toast.error('Configuration d\'organisation introuvable. Veuillez réessayer dans quelques instants.');
            console.error('orgaConfigId est null, impossible de lancer le live');
            return;
        }

        // Determine action based on current state
        const action = isLive ? 'stop-live' : 'start-live';

        try {
            setStartError(null);

            // Vérifier que le username est fourni pour démarrer un live
            if (!isLive && !tiktokUsername?.trim()) {
                toast.error('Veuillez entrer un nom de compte TikTok');
                return;
            }

            if (!isLive && isUsernameActive) {
                toast.error('Un live est déjà actif pour ce compte TikTok');
                return;
            }

            const body = isLive ? {} : { tiktokUsername: tiktokUsername.trim() };
            if (action === 'stop-live') {
                setIsStopping(true);
            } else if (action === 'start-live') {
                setIsStarting(true);
            }

            const res = await fetch(`${LIVE_ASSISTANT_BASE_URL}/${action}/${orgaConfigId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            });

            if (!res.ok) {
                if (action === 'stop-live' && res.status === 404) {
                    setLiveSession(null);
                    toast.success("Aucune session active côté serveur. État corrigé.");
                    return;
                }
                let msg = 'Erreur lors du lancement du live';
                try {
                    const data = await res.json();
                    // Prioriser la raison détaillée, puis error, puis message
                    msg = data?.reason || data?.error || data?.message || msg;
                } catch { }
                setStartError(msg);
                toast.error(msg);
                return;
            }

            if (isLive) {
                setLiveSession(null);
                toast.success('Live arrêté');
            } else {
                toast.success('Live lancé');
            }
            console.log(`✅ Live ${isLive ? 'stopped' : 'started'} successfully`);

        } catch (error) {
            const msg = (error as any)?.message || 'Erreur lors du lancement du live';
            setStartError(msg);
            toast.error(msg);
            console.error('Erreur lors du toggle live:', error);
        }
        finally {
            if (action === 'stop-live') {
                setIsStopping(false);
            } else if (action === 'start-live') {
                setIsStarting(false);
            }
        }
    };

    // Styles dynamiques basés sur le thème
    const themeColors = theme.colors;

    return (
        <div
            className="flex flex-col h-full overflow-y-auto py-5"
            style={{
                backgroundColor: themeColors.backgroundSecondary || themeColors.background || '#F9FAFB'
            }}
        >
            <SeoTags
                title={`Assistant Live | CashLive`}
                description={`Assistant IA pour optimiser vos performances en live`}
                url={typeof window !== 'undefined' ? window.location.href : ''}
                noIndex={true}
            />
            {/* Compact Header */}
            <div
                className="sticky top-0 z-10 backdrop-blur-md shadow-sm "
                style={{
                    backgroundColor: `${themeColors.background || '#FFFFFF'}CC`,
                    borderBottomColor: themeColors.border || '#E5E7EB'
                }}
            >
                <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b" style={{ borderBottomColor: themeColors.border || '#E5E7EB' }}>
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                        <div
                            className="p-2.5 rounded-xl shadow-md"
                            style={{
                                background: isLive
                                    ? `linear-gradient(135deg, ${themeColors.error || '#DC2626'} 0%, ${themeColors.errorDark || '#991B1B'} 100%)`
                                    : `linear-gradient(135deg, ${themeColors.primary || '#9333EA'} 0%, ${themeColors.primaryDark || '#7C3AED'} 100%)`,
                            }}
                        >
                            <Brain
                                size={22}
                                style={{
                                    color: '#FFFFFF',
                                    strokeWidth: 2.5
                                }}
                            />
                        </div>
                        <div className="min-w-0 flex-1">
                            <h1
                                className="text-base sm:text-lg font-bold flex items-center gap-2 truncate"
                                style={{ color: themeColors.foreground || '#111827' }}
                            >
                                <span>Assistant IA Live</span>
                            </h1>
                            <p
                                className="text-xs flex items-center gap-2 flex-wrap"
                                style={{ color: themeColors.foregroundSecondary || '#6B7280' }}
                            >
                                {isLive ? (
                                    <>
                                        <span
                                            className="flex items-center gap-1.5 font-medium"
                                            style={{ color: themeColors.error || '#DC2626' }}
                                        >
                                            <span
                                                className="w-1.5 h-1.5 rounded-full animate-pulse"
                                                style={{ backgroundColor: themeColors.error || '#DC2626' }}
                                            />
                                            EN DIRECT
                                        </span>
                                        {liveSession?.roomId && (
                                            <span
                                                className="border-l pl-2"
                                                style={{
                                                    color: themeColors.foregroundSecondary || '#9CA3AF',
                                                    borderLeftColor: themeColors.border || '#D1D5DB'
                                                }}
                                            >
                                                ID: {liveSession.roomId}
                                            </span>
                                        )}
                                        {lastHeartbeatLabel && (
                                            <span
                                                className="border-l pl-2"
                                                style={{
                                                    color: themeColors.foregroundSecondary || '#9CA3AF',
                                                    borderLeftColor: themeColors.border || '#D1D5DB'
                                                }}
                                            >
                                                Dernier heartbeat: {lastHeartbeatLabel}
                                            </span>
                                        )}
                                    </>
                                ) : creatorStatus?.state === 'CRASHED' ? (
                                    <span
                                        className="font-medium flex items-center gap-2"
                                        style={{ color: themeColors.error || '#DC2626' }}
                                    >
                                        <Activity size={14} />
                                        Session interrompue (Crash)
                                    </span>
                                ) : (
                                    "IA pour gérer au top votre live TikTok"
                                )}
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 lg:gap-6 w-full sm:w-auto">
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                            {/* Si pas de crédits, pas de plan PRO et pas de live actif, afficher uniquement le message de recharge */}
                            {!configLoading && !hasCredits && !isIAUnlimited && !isLive ? (
                                <div className="flex items-center gap-3 px-4 py-2.5 bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-lg shadow-md">
                                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                                    <span className="text-sm font-semibold text-amber-900">
                                        Recharger les crédits pour utiliser l'IA
                                    </span>
                                    <button
                                        onClick={handleBuyCredits}
                                        className="px-4 py-2.5 bg-gradient-to-r from-primary to-purple-600 text-white rounded-lg text-sm font-bold transition-all hover:from-primary-dark hover:to-purple-700 hover:scale-105 active:scale-95 shadow-lg shadow-primary/30 flex items-center gap-2 whitespace-nowrap"
                                    >
                                        <ShoppingCart size={16} strokeWidth={2.5} />
                                        Recharger
                                    </button>
                                </div>
                            ) : (canStartLive || isLive) ? (
                                <>
                                    {!isLive && (
                                        <div className="relative flex-1 sm:flex-initial">
                                            <input
                                                type="text"
                                                placeholder="Compte TikTok"
                                                value={tiktokUsername}
                                                onChange={(e) => setTiktokUsername(e.target.value)}
                                                className="w-full sm:w-48 pl-3 pr-3 py-2 rounded-lg border text-sm shadow-sm transition-all focus:outline-none focus:ring-2 focus:border-transparent"
                                                style={{
                                                    backgroundColor: (isUsernameActive || startError)
                                                        ? themeColors.errorLight || '#FEE2F2'
                                                        : themeColors.surface || '#FFFFFF',
                                                    borderColor: (isUsernameActive || startError)
                                                        ? themeColors.error || '#DC2626'
                                                        : themeColors.border || '#D1D5DB',
                                                    color: themeColors.foreground || '#111827',
                                                    '--tw-ring-color': themeColors.primary || '#9333EA',
                                                } as React.CSSProperties & { '--tw-ring-color': string }}
                                                onFocus={(e) => {
                                                    e.target.style.borderColor = themeColors.primary || '#9333EA';
                                                    e.target.style.boxShadow = `0 0 0 2px ${themeColors.primaryLight || '#F3E8FF'}`;
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.borderColor = (isUsernameActive || startError)
                                                        ? themeColors.error || '#DC2626'
                                                        : themeColors.border || '#D1D5DB';
                                                    e.target.style.boxShadow = 'none';
                                                }}
                                            />
                                            <style>{`
                                                input[type="text"]::placeholder {
                                                    color: ${themeColors.foregroundSecondary || '#9CA3AF'} !important;
                                                }
                                            `}</style>
                                            {isUsernameActive && (
                                                <div
                                                    className="mt-1 text-xs"
                                                    style={{ color: themeColors.error || '#DC2626' }}
                                                >
                                                    Live déjà actif pour ce compte
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <button
                                        onClick={toggleLive}
                                        disabled={isStopping || isStarting || (!isLive && (!tiktokUsername || isUsernameActive) && creatorStatus?.state !== 'CRASHED')}
                                        className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm text-white transition-all shadow-md hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed w-full sm:w-auto"
                                        style={{
                                            backgroundColor: isLive
                                                ? themeColors.error || '#DC2626'
                                                : creatorStatus?.state === 'CRASHED'
                                                    ? themeColors.warning || '#F97316'
                                                    : (!tiktokUsername || isUsernameActive)
                                                        ? themeColors.neutral300 || '#D1D5DB'
                                                        : themeColors.success || '#10B981',
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!e.currentTarget.disabled) {
                                                e.currentTarget.style.backgroundColor = isLive
                                                    ? themeColors.errorDark || '#991B1B'
                                                    : creatorStatus?.state === 'CRASHED'
                                                        ? themeColors.warningDark || '#EA580C'
                                                        : themeColors.successDark || '#059669';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = isLive
                                                ? themeColors.error || '#DC2626'
                                                : creatorStatus?.state === 'CRASHED'
                                                    ? themeColors.warning || '#F97316'
                                                    : (!tiktokUsername || isUsernameActive)
                                                        ? themeColors.neutral300 || '#D1D5DB'
                                                        : themeColors.success || '#10B981';
                                        }}
                                    >
                                        {isStarting ? (
                                            <>
                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                DÉMARRAGE...
                                            </>
                                        ) : isStopping ? (
                                            <>
                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                ARRÊT...
                                            </>
                                        ) : isLive ? (
                                            <>
                                                <Square size={16} fill="currentColor" />
                                                ARRÊTER
                                            </>
                                        ) : creatorStatus?.state === 'CRASHED' ? (
                                            <>
                                                <Activity size={16} />
                                                RELANCER
                                            </>
                                        ) : (
                                            <>
                                                <Play size={16} fill="currentColor" />
                                                LANCER LE LIVE
                                            </>
                                        )}
                                    </button>
                                </>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content - Compact Grid */}
            <div className="max-w-[1600px] mx-auto w-full px-3 sm:px-6 py-4">
                {startError && (
                    <div
                        className="mb-4 border-l-4 rounded-lg p-4 flex items-start gap-3 shadow-sm"
                        style={{
                            backgroundColor: themeColors.errorLight || '#FEF2F2',
                            borderLeftColor: themeColors.error || '#DC2626',
                        }}
                    >
                        <AlertCircle
                            className="w-5 h-5 flex-shrink-0 mt-0.5"
                            style={{ color: themeColors.error || '#DC2626' }}
                        />
                        <div className="flex-1">
                            <div
                                className="text-sm font-semibold mb-1"
                                style={{ color: themeColors.errorDark || '#7F1D1D' }}
                            >
                                Erreur lors du démarrage du live
                            </div>
                            <div
                                className="text-sm leading-relaxed"
                                style={{ color: themeColors.error || '#991B1B' }}
                            >
                                {startError}
                            </div>
                        </div>
                        <button
                            onClick={() => setStartError(null)}
                            className="flex-shrink-0 p-1 rounded-md transition-colors"
                            style={{
                                color: themeColors.error || '#991B1B'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(220, 38, 38, 0.1)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                            aria-label="Fermer le message d'erreur"
                        >
                            <X size={18} />
                        </button>
                    </div>
                )}
                {
                    isLive && !isStarting && liveSession && isDesync && (
                        <div
                            className="mb-3 border rounded-lg p-3 flex items-center justify-between"
                            style={{
                                backgroundColor: themeColors.warningLight || '#FEF3C7',
                                borderColor: themeColors.warning || '#F59E0B',
                                borderLeftWidth: '4px',
                                borderLeftColor: themeColors.warning || '#F59E0B'
                            }}
                        >
                            <div
                                className="text-sm flex-1"
                                style={{ color: themeColors.warningDark || '#92400E' }}
                            >
                                Possible désynchronisation: aucune donnée reçue depuis plus de 2 minutes.
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={forceStop}
                                    disabled={isForcingStop}
                                    className="px-3 py-1.5 text-xs font-semibold rounded text-white disabled:opacity-50 transition-colors"
                                    style={{
                                        backgroundColor: themeColors.error || '#DC2626'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!e.currentTarget.disabled) {
                                            e.currentTarget.style.backgroundColor = themeColors.errorDark || '#991B1B';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = themeColors.error || '#DC2626';
                                    }}
                                >
                                    FORCER L'ARRÊT
                                </button>
                            </div>
                        </div>
                    )
                }
                {
                    !isLive ? (
                        <div className="space-y-6">
                            {creatorStatus?.state === 'CRASHED' && (
                                <div
                                    className="border rounded-lg p-4 max-w-2xl mx-auto text-center mb-6"
                                    style={{
                                        backgroundColor: themeColors.errorLight || '#FEF2F2',
                                        borderColor: themeColors.error || '#DC2626',
                                    }}
                                >
                                    <h3
                                        className="font-bold text-lg mb-1"
                                        style={{ color: themeColors.errorDark || '#7F1D1D' }}
                                    >
                                        Le live a crashé 😱
                                    </h3>
                                    <p
                                        className="text-sm mb-3 leading-relaxed"
                                        style={{ color: themeColors.error || '#991B1B' }}
                                    >
                                        La connexion avec TikTok a été perdue ou une erreur est survenue.
                                    </p>
                                    <button
                                        onClick={toggleLive}
                                        className="px-4 py-2 text-white rounded-lg font-semibold transition-colors shadow-sm"
                                        style={{
                                            backgroundColor: themeColors.error || '#DC2626'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = themeColors.errorDark || '#991B1B';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = themeColors.error || '#DC2626';
                                        }}
                                    >
                                        Relancer maintenant
                                    </button>
                                </div>
                            )}

                            {/* Header Preview */}
                            <div className="text-center max-w-3xl mx-auto space-y-3 px-3 sm:px-6">
                                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border" style={{
                                    backgroundColor: `${themeColors.primary || '#9333EA'}15`,
                                    borderColor: `${themeColors.primary || '#9333EA'}30`
                                }}>
                                    <Activity size={20} style={{ color: themeColors.primary || '#9333EA' }} />
                                    <span className="text-sm font-semibold" style={{ color: themeColors.primary || '#9333EA' }}>
                                        Aperçu de l'interface Live
                                    </span>
                                </div>
                                <h2
                                    className="text-xl sm:text-2xl font-bold"
                                    style={{ color: themeColors.foreground || '#111827' }}
                                >
                                    Voici ce que vous verrez une fois le live lancé
                                </h2>
                                <p
                                    className="text-xs sm:text-sm leading-relaxed"
                                    style={{ color: themeColors.foregroundSecondary || '#6B7280' }}
                                >
                                    L'assistant IA analysera en temps réel votre live TikTok et vous fournira des recommandations personnalisées pour maximiser l'engagement et les ventes.
                                </p>
                            </div>

                            {/* Skeleton Preview Grid */}
                            <div className="grid grid-cols-12 gap-3 sm:gap-4 px-3 sm:px-6 opacity-60">
                                {/* Left Column - Stats & Context */}
                                <div className="col-span-12 lg:col-span-3 flex flex-col gap-3">
                                    {/* Stats Card */}
                                    <div className="rounded-xl p-4 border" style={{
                                        backgroundColor: themeColors.surface || '#FFFFFF',
                                        borderColor: themeColors.border || '#E5E7EB'
                                    }}>
                                        <div className="flex items-center justify-between mb-3">
                                            <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: themeColors.foregroundSecondary || '#6B7280' }}>
                                                📊 Statistiques Live
                                            </span>
                                        </div>
                                        <div className="space-y-2">
                                            <div className="h-10 rounded animate-pulse" style={{ backgroundColor: `${themeColors.primary || '#9333EA'}20` }} />
                                            <div className="h-10 rounded animate-pulse" style={{ backgroundColor: `${themeColors.success || '#10B981'}20` }} />
                                            <div className="h-10 rounded animate-pulse" style={{ backgroundColor: `${themeColors.warning || '#F59E0B'}20` }} />
                                        </div>
                                    </div>

                                    {/* JP Events */}
                                    <div className="rounded-xl p-4 border" style={{
                                        backgroundColor: themeColors.surface || '#FFFFFF',
                                        borderColor: themeColors.border || '#E5E7EB'
                                    }}>
                                        <div className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: themeColors.foregroundSecondary || '#6B7280' }}>
                                            🛒 Intentions d'achat
                                        </div>
                                        <div className="space-y-2">
                                            <div className="h-8 rounded animate-pulse" style={{ backgroundColor: `${themeColors.foregroundSecondary || '#6B7280'}10` }} />
                                            <div className="h-8 rounded animate-pulse" style={{ backgroundColor: `${themeColors.foregroundSecondary || '#6B7280'}10` }} />
                                        </div>
                                    </div>

                                    {/* Top Followers */}
                                    <div className="rounded-xl p-4 border" style={{
                                        backgroundColor: themeColors.surface || '#FFFFFF',
                                        borderColor: themeColors.border || '#E5E7EB'
                                    }}>
                                        <div className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: themeColors.foregroundSecondary || '#6B7280' }}>
                                            ⭐ Top Engagés
                                        </div>
                                        <div className="space-y-2">
                                            <div className="h-6 rounded animate-pulse" style={{ backgroundColor: `${themeColors.foregroundSecondary || '#6B7280'}10` }} />
                                            <div className="h-6 rounded animate-pulse" style={{ backgroundColor: `${themeColors.foregroundSecondary || '#6B7280'}10` }} />
                                            <div className="h-6 rounded animate-pulse" style={{ backgroundColor: `${themeColors.foregroundSecondary || '#6B7280'}10` }} />
                                        </div>
                                    </div>
                                </div>

                                {/* Center Column - AI Suggestions */}
                                <div className="col-span-12 lg:col-span-6 flex flex-col gap-3">
                                    {/* Main Suggestions */}
                                    <div className="rounded-xl p-5 border" style={{
                                        backgroundColor: themeColors.surface || '#FFFFFF',
                                        borderColor: themeColors.border || '#E5E7EB'
                                    }}>
                                        <div className="flex items-center gap-2 mb-4">
                                            <Brain size={20} style={{ color: themeColors.primary || '#9333EA' }} />
                                            <span className="text-sm font-bold" style={{ color: themeColors.foreground || '#111827' }}>
                                                Suggestions IA en temps réel
                                            </span>
                                        </div>
                                        <div className="space-y-3">
                                            <div className="h-16 rounded-lg animate-pulse" style={{ backgroundColor: `${themeColors.primary || '#9333EA'}15` }} />
                                            <div className="h-16 rounded-lg animate-pulse" style={{ backgroundColor: `${themeColors.success || '#10B981'}15` }} />
                                            <div className="h-16 rounded-lg animate-pulse" style={{ backgroundColor: `${themeColors.warning || '#F59E0B'}15` }} />
                                        </div>
                                    </div>

                                    {/* Actions IA */}
                                    <div className="rounded-xl p-5 border" style={{
                                        backgroundColor: themeColors.surface || '#FFFFFF',
                                        borderColor: themeColors.border || '#E5E7EB'
                                    }}>
                                        <div className="text-sm font-bold mb-3" style={{ color: themeColors.foreground || '#111827' }}>
                                            👉 Actions recommandées
                                        </div>
                                        <div className="space-y-2">
                                            <div className="h-12 rounded animate-pulse" style={{ backgroundColor: `${themeColors.foregroundSecondary || '#6B7280'}10` }} />
                                            <div className="h-12 rounded animate-pulse" style={{ backgroundColor: `${themeColors.foregroundSecondary || '#6B7280'}10` }} />
                                        </div>
                                    </div>
                                </div>

                                {/* Right Column - Orders */}
                                <div className="col-span-12 lg:col-span-3">
                                    <div className="rounded-xl p-4 border" style={{
                                        backgroundColor: themeColors.surface || '#FFFFFF',
                                        borderColor: themeColors.border || '#E5E7EB'
                                    }}>
                                        <div className="flex items-center gap-2 mb-3">
                                            <ShoppingCart size={18} style={{ color: themeColors.success || '#10B981' }} />
                                            <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: themeColors.foregroundSecondary || '#6B7280' }}>
                                                Commandes récentes
                                            </span>
                                        </div>
                                        <div className="space-y-2">
                                            <div className="h-20 rounded-lg animate-pulse" style={{ backgroundColor: `${themeColors.success || '#10B981'}15` }} />
                                            <div className="h-20 rounded-lg animate-pulse" style={{ backgroundColor: `${themeColors.success || '#10B981'}15` }} />
                                            <div className="h-20 rounded-lg animate-pulse" style={{ backgroundColor: `${themeColors.success || '#10B981'}15` }} />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* CTA */}
                            <div className="text-center pt-4 px-3 sm:px-6">
                                {!hasCredits && !isIAUnlimited ? (
                                    <div className="max-w-md mx-auto">
                                        <div className="p-4 rounded-lg bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 mb-4 shadow-md">
                                            <AlertCircle className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                                            <p className="text-sm font-semibold text-amber-900 mb-3">
                                                Crédits requis pour lancer l'assistant IA
                                            </p>
                                            <button
                                                onClick={handleBuyCredits}
                                                className="px-4 py-2.5 bg-gradient-to-r from-primary to-purple-600 text-white rounded-lg text-sm font-bold transition-all hover:from-primary-dark hover:to-purple-700 hover:scale-105 active:scale-95 shadow-lg shadow-primary/30 flex items-center gap-2 mx-auto"
                                            >
                                                <ShoppingCart size={16} />
                                                Recharger maintenant
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <p className="text-xs sm:text-sm font-medium mb-3" style={{ color: themeColors.foregroundSecondary || '#6B7280' }}>
                                            Prêt à démarrer ? Entrez votre nom de compte TikTok ci-dessus et cliquez sur
                                        </p>
                                        <div className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-bold text-white shadow-lg" style={{
                                            backgroundColor: themeColors.success || '#10B981'
                                        }}>
                                            <Play size={18} fill="currentColor" />
                                            LANCER LE LIVE
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    ) : isLive && (liveSession || suggestionDoc || snapshotDoc) ? (
                        <div className="grid grid-cols-12 gap-3 sm:gap-4">
                            {/* Left Column: Stats & Context (3 cols) */}
                            <div className="col-span-12 lg:col-span-3 flex flex-col gap-3">
                                <LiveStats stats={stats} theme={theme} />
                                <LiveJp jpEvents={jpEvents} theme={theme} />
                                <LiveFollowers followers={followers} theme={theme} />
                            </div>

                            {/* Center Column: AI Suggestions (6 cols) */}
                            <div className="col-span-12 lg:col-span-6 flex flex-col gap-3">
                                <LiveSuggestions suggestions={suggestions} timestamp={suggestionDoc?.createdAt} theme={theme} />
                                <LiveActions actions={actions} timestamp={suggestionDoc?.createdAt} theme={theme} />
                            </div>

                            {/* Right Column: Orders (3 cols) */}
                            <div className="col-span-12 lg:col-span-3">
                                <LiveOrders orders={orders} theme={theme} />
                            </div>
                        </div>
                    ) : (isLive || isStarting) && !liveSession && !suggestionDoc && !snapshotDoc && !currentSessionId ? (
                        <div className="flex flex-col items-center justify-center h-[calc(100vh-140px)]">
                            <div className="text-center space-y-4">
                                <div
                                    className="w-20 h-20 rounded-full flex items-center justify-center mx-auto shadow-sm border"
                                    style={{
                                        backgroundColor: themeColors.surface || '#FFFFFF',
                                        borderColor: themeColors.borderLight || '#F3F4F6'
                                    }}
                                >
                                    <div
                                        className="w-8 h-8 border-4 rounded-full animate-spin"
                                        style={{
                                            borderColor: `${themeColors.primary || '#9333EA'}33`,
                                            borderTopColor: themeColors.primary || '#9333EA'
                                        }}
                                    />
                                </div>
                                <h2
                                    className="text-xl font-semibold"
                                    style={{ color: themeColors.foreground || '#374151' }}
                                >
                                    Chargement de la session...
                                </h2>
                                <p
                                    className="max-w-md mx-auto text-sm"
                                    style={{ color: themeColors.foregroundSecondary || '#6B7280' }}
                                >
                                    {isStarting ? 'Démarrage du live en cours...' : 'Connexion au live TikTok en cours...'}
                                </p>
                            </div>
                        </div>
                    ) : null
                }
            </div >

            {/* Purchase Modal */}
            {idorga && (
                <BuyCreditsModal
                    isOpen={showPurchaseModal}
                    onClose={handleClosePurchaseModal}
                    organizationId={idorga}
                    currentBalance={balanceCents}
                />
            )}
        </div >
    );
};
